import { useState, useCallback } from 'react';
import { Layout } from '@vwfs-bronson/bronson-react';

import { BASE_CONFIGURATION } from '../components/ConfigurationCreator/constants.js';
import {
  applyApplicationValue,
  deepClone,
  getByPath,
  setByPath,
} from '../components/ConfigurationCreator/utils.js';
import { Section } from '../components/ConfigurationCreator/Section.jsx';
import { renderConfigurationNode } from '../components/ConfigurationCreator/ConfigurationTreeRenderer.jsx';
import { HiddenFieldsEditor } from '../components/ConfigurationCreator/HiddenFieldsEditor.jsx';
import { GeneratedJsonPanel } from '../components/ConfigurationCreator/GeneratedJsonPanel.jsx';
import CoreService from '../services/CoreService.js';

const getReturnUrl = (payload) =>
  payload?.header?.returnUrl ||
  payload?.header?.ReturnUrl ||
  payload?.Header?.returnUrl ||
  payload?.Header?.ReturnUrl ||
  '';

const getApiErrors = (response, payload) => {
  if (Array.isArray(response?.errors)) {
    return response.errors;
  }

  if (Array.isArray(payload?.errors)) {
    return payload.errors;
  }

  if (Array.isArray(response?.error)) {
    return response.error;
  }

  return [];
};

export const ConfigurationCreatorPage = () => {
  const [configuration, setConfiguration] = useState(() => deepClone(BASE_CONFIGURATION));
  const [applicationBaseValue, setApplicationBaseValue] = useState(
    BASE_CONFIGURATION.Header.Application,
  );
  const [applicationWithPrefixC, setApplicationWithPrefixC] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [generatedLinkError, setGeneratedLinkError] = useState('');

  const handleChange = useCallback((path, value) => {
    setConfiguration((previous) => setByPath(previous, path, value));
  }, []);

  const getHiddenFieldCurrentValue = useCallback(
    (fieldPath) => getByPath(configuration, fieldPath.split('.')),
    [configuration],
  );

  const handleSetHiddenField = useCallback((fieldPath, value) => {
    setConfiguration((previous) => setByPath(previous, fieldPath.split('.'), value));
  }, []);

  const handleReset = () => {
    setConfiguration(deepClone(BASE_CONFIGURATION));
    setApplicationBaseValue(BASE_CONFIGURATION.Header.Application);
    setApplicationWithPrefixC(false);
    setGeneratedLink('');
    setGeneratedLinkError('');
  };

  const handleApplicationBaseChange = (baseValue) => {
    setApplicationBaseValue(baseValue);
    handleChange(
      ['Header', 'Application'],
      applyApplicationValue(baseValue, applicationWithPrefixC),
    );
  };

  const handleApplicationPrefixToggle = (checked) => {
    setApplicationWithPrefixC(checked);
    handleChange(
      ['Header', 'Application'],
      applyApplicationValue(applicationBaseValue, checked),
    );
  };

  const handleGenerateLink = async () => {
    setIsGeneratingLink(true);
    setGeneratedLink('');
    setGeneratedLinkError('');

    try {
      const coreService = new CoreService();
      const response = await coreService.configuration(configuration);
      const payload = response?.data ?? response?.body ?? response;

      const returnUrl = getReturnUrl(payload);
      const apiErrors = getApiErrors(response, payload);

      if (returnUrl) {
        setGeneratedLink(returnUrl);
      } else if (apiErrors.length === 0) {
        setGeneratedLinkError('No se recibió returnUrl en la respuesta.');
      }

      if (apiErrors.length > 0) {
        setGeneratedLinkError(
          apiErrors
            .map((errorItem) => errorItem?.value || errorItem?.message || String(errorItem))
            .join(' | '),
        );
      }
    } catch (error) {
      setGeneratedLinkError(error?.message || 'Error al generar link.');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  return (
    <Layout>
      <Layout.Item default="2/3" s="1/1">
        <Section title="Configurador JSON">
          {Object.keys(configuration).map((sectionKey) => (
            <Section key={sectionKey} title={sectionKey}>
              {renderConfigurationNode(
                getByPath(configuration, [sectionKey]),
                [sectionKey],
                handleChange,
                {
                  baseValue: applicationBaseValue,
                  withPrefixC: applicationWithPrefixC,
                  onBaseChange: handleApplicationBaseChange,
                  onTogglePrefix: handleApplicationPrefixToggle,
                },
              )}
            </Section>
          ))}

          <HiddenFieldsEditor
            getCurrentValue={getHiddenFieldCurrentValue}
            setValue={handleSetHiddenField}
          />
        </Section>
      </Layout.Item>

      <Layout.Item default="1/3" s="1/1">
        <GeneratedJsonPanel
          configuration={configuration}
          onGenerateLink={handleGenerateLink}
          onReset={handleReset}
          isGeneratingLink={isGeneratingLink}
          generatedLink={generatedLink}
          generatedLinkError={generatedLinkError}
        />
      </Layout.Item>
    </Layout>
  );
};
